import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Check, X, Loader2 } from "lucide-react";
import notificationService from "../../services/notificationService";

/**
 * Componente de botão para ativar/desativar notificações push
 * Usar em Admin, Fornecedor e Afiliado
 */
const NotificationToggle = ({ usuarioId, tipoUsuario, variant = "default" }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const status = notificationService.getPermissionStatus();
      setPermission(status);
      
      if (status === 'granted') {
        const subscribed = await notificationService.isSubscribed();
        setIsSubscribed(subscribed);
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      setLoading(true);
      
      if (isSubscribed) {
        // Desinscrever
        await notificationService.unsubscribe();
        setIsSubscribed(false);
        toast.success('🔕 Notificações desativadas');
      } else {
        // Inscrever
        await notificationService.subscribe(usuarioId, tipoUsuario);
        setIsSubscribed(true);
        toast.success('🔔 Notificações ativadas!');
        
        // Enviar notificação de teste
        setTimeout(async () => {
          try {
            await notificationService.sendTest(usuarioId, tipoUsuario);
          } catch (e) {
            console.log('Teste de notificação enviado');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao alterar notificações:', error);
      
      if (error.message.includes('negada')) {
        toast.error('Permissão de notificações negada. Ative nas configurações do navegador.');
      } else {
        toast.error('Erro ao alterar configuração de notificações');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verificar suporte
  if (!notificationService.isSupported) {
    return null; // Não mostrar nada se não suportado
  }

  // Variante compacta (apenas ícone)
  if (variant === "icon") {
    return (
      <Button
        onClick={handleToggle}
        disabled={loading}
        size="sm"
        variant="outline"
        className={`h-8 w-8 p-0 ${isSubscribed ? 'border-green-500 text-green-500' : 'border-gray-600'}`}
        title={isSubscribed ? 'Notificações ativadas' : 'Ativar notificações'}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="w-4 h-4" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
      </Button>
    );
  }

  // Variante padrão (botão com texto)
  return (
    <Button
      onClick={handleToggle}
      disabled={loading}
      className={`h-9 ${
        isSubscribed 
          ? 'bg-green-500 hover:bg-green-600' 
          : 'bg-gray-700 hover:bg-gray-600'
      }`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : isSubscribed ? (
        <BellRing className="w-4 h-4 mr-2" />
      ) : (
        <Bell className="w-4 h-4 mr-2" />
      )}
      {isSubscribed ? 'Notificações Ativas' : 'Ativar Notificações'}
    </Button>
  );
};

/**
 * Card explicativo para ativar notificações (primeira vez)
 */
const NotificationPrompt = ({ usuarioId, tipoUsuario, onDismiss }) => {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Verificar se já foi mostrado antes
  useEffect(() => {
    const key = `notification_prompt_${tipoUsuario}`;
    const wasDismissed = localStorage.getItem(key);
    if (wasDismissed) setDismissed(true);
  }, [tipoUsuario]);

  const handleActivate = async () => {
    try {
      setLoading(true);
      await notificationService.subscribe(usuarioId, tipoUsuario);
      toast.success('🔔 Notificações ativadas com sucesso!');
      handleDismiss();
    } catch (error) {
      if (error.message.includes('negada')) {
        toast.error('Permissão negada. Ative nas configurações do navegador.');
      } else {
        toast.error('Erro ao ativar notificações');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    const key = `notification_prompt_${tipoUsuario}`;
    localStorage.setItem(key, 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  if (dismissed || !notificationService.isSupported) return null;

  const messages = {
    admin: {
      title: '🔔 Receba alertas de vendas!',
      body: 'Ative as notificações para ser alertado sobre novas vendas e atividades importantes na plataforma.'
    },
    fornecedor: {
      title: '📦 Não perca nenhum pedido!',
      body: 'Ative as notificações para ser alertado quando novos pedidos chegarem para separação no WMS.'
    },
    afiliado: {
      title: '💰 Saiba quando ganhar comissões!',
      body: 'Ative as notificações para ser alertado sobre novas comissões e atualizações importantes.'
    }
  };

  const msg = messages[tipoUsuario] || messages.afiliado;

  return (
    <Card className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-orange-500/30 p-4 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold">{msg.title}</h3>
            <p className="text-gray-300 text-sm mt-1">{msg.body}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-gray-400 hover:text-white h-8 w-8 p-0"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex gap-2 mt-3 ml-13">
        <Button
          onClick={handleActivate}
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          Ativar Agora
        </Button>
        <Button
          onClick={handleDismiss}
          variant="outline"
          className="border-gray-600"
        >
          Depois
        </Button>
      </div>
    </Card>
  );
};

export { NotificationToggle, NotificationPrompt };
export default NotificationToggle;
